import type { Action } from '@ngrx/store';
import { createReducer, on } from '@ngrx/store';
import type { EntityState, EntityAdapter } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';

import * as ArticlesActions from './articles.actions';
import type { ArticleEntity } from './articles.models';

export const ARTICLES_FEATURE_KEY = 'articles';

export interface ArticlesState extends EntityState<ArticleEntity> {
  articleIdOfCurrentPage: string | null;
  articleIdOfCurrentPreviewPage: string | null;
}

export interface ArticlesPartialState {
  readonly [ARTICLES_FEATURE_KEY]: ArticlesState;
}

export const articlesAdapter: EntityAdapter<ArticleEntity> = createEntityAdapter<ArticleEntity>({
  selectId: (article: ArticleEntity) => article.id,
});

export const initialState: ArticlesState = articlesAdapter.getInitialState({
  articleIdOfCurrentPage: null,
  articleIdOfCurrentPreviewPage: null,
});

const articlesReducer = createReducer<ArticlesState>(
  initialState,
  on(
    ArticlesActions.loadArticle,
    (state: ArticlesState, { id }): ArticlesState => articlesAdapter.upsertOne({ id, error: null }, { ...state })
  ),
  on(
    ArticlesActions.loadArticleSuccess,
    (state, { article, id }): ArticlesState =>
      articlesAdapter.upsertOne(
        {
          id,
          articleData: article,
          fetchDate: new Date().toISOString(),
          error: null,
        },
        { ...state }
      )
  ),
  on(
    ArticlesActions.loadArticleFailure,
    (state: ArticlesState, { id, error }): ArticlesState => articlesAdapter.upsertOne({ id, error }, { ...state })
  ),
  on(
    ArticlesActions.selectPreviewArticle,
    (state: ArticlesState, { id }): ArticlesState => ({ ...state, articleIdOfCurrentPreviewPage: id })
  ),
  on(
    ArticlesActions.selectArticle,
    (state: ArticlesState, { id }): ArticlesState => ({ ...state, articleIdOfCurrentPage: id })
  ),
  on(
    ArticlesActions.unselectPreviewArticle,
    (state: ArticlesState): ArticlesState => ({ ...state, articleIdOfCurrentPreviewPage: null })
  ),
  on(
    ArticlesActions.unselectArticle,
    (state: ArticlesState): ArticlesState => ({ ...state, articleIdOfCurrentPage: null })
  )
);

export function reducer(state: ArticlesState | undefined, action: Action): ArticlesState {
  return articlesReducer(state, action);
}
